// Pin identity across order edits (#188). Pinned order cards are keyed by
// venue order id, but an edit may route through cancel + place — which mints
// a NEW id and would strand the pin on the retired order (it renders as a
// tombstone and the fresh order arrives unpinned). This module owns the two
// pieces the fix needs outside the big component: the key algebra (which pin
// key an order id maps to, and how a pin moves between keys) and the
// success/failure ordering (transfer only after the venue edit resolved,
// never on a same-id edit, never on a failed one).

/** A pinned watch-copy in the tracker's pinned strip. Keyed by stable card
 *  identity (position = ticker+side, order = venue order id); `label` is
 *  snapshotted at pin time so a tombstone can still name itself. Persisted
 *  under `g1mt_tracker_pins`. */
export interface PinnedEntry {
  key: string;
  kind: 'position' | 'order';
  label: string;
}

export type OrderVenue = 'kalshi' | 'polymarket';

/** Pin key for an order, mirroring OrderCardVM.key: Kalshi pins use the raw
 *  venue order id; Polymarket pins are `poly-${id}` (the prefix keeps the two
 *  venues' ids from colliding in the shared key namespace). `editOrder`
 *  carries the raw cancel id for BOTH venues, so edit-path callers must map
 *  through this before touching pin state. */
export function orderPinKey(venue: OrderVenue, orderId: string): string {
  return venue === 'polymarket' ? `poly-${orderId}` : orderId;
}

/** Move an order pin from `previousKey` to `replacementKey` in place.
 *
 *  Only entries with `kind === 'order'` are eligible — a position pin whose
 *  key happens to collide with an order id is never touched. The transferred
 *  entry keeps its array slot and snapshotted label. If the replacement key
 *  is somehow already pinned, the stale previous-key entry is dropped and
 *  exactly one replacement-key entry survives. No-op cases (same key, or the
 *  previous key isn't pinned) return the input array unchanged so callers
 *  inside a functional setState don't churn state or re-persist. */
export function transferOrderPin(
  entries: PinnedEntry[],
  previousKey: string,
  replacementKey: string,
): PinnedEntry[] {
  if (previousKey === replacementKey) return entries;
  const isPreviousOrderPin = (e: PinnedEntry) => e.kind === 'order' && e.key === previousKey;
  if (!entries.some(isPreviousOrderPin)) return entries;
  const replacementAlreadyPinned = entries.some(
    (e) => e.kind === 'order' && e.key === replacementKey,
  );
  if (replacementAlreadyPinned) {
    return entries.filter((e) => !isPreviousOrderPin(e));
  }
  return entries.map((e) => (isPreviousOrderPin(e) ? { ...e, key: replacementKey } : e));
}

/** Run a venue order edit and transfer the pin iff it succeeded under a new
 *  id. Ordering contract (#188): the pin must never move before the venue
 *  confirmed the replacement — a failed edit leaves the old pin exactly where
 *  it was (tombstoning if the cancel leg already destroyed the order is the
 *  issue's required failure behavior). Same-id success (no-op / queue-
 *  preserving decrease) never calls `transfer`. */
export async function runOrderEditWithPinTransfer(
  previousOrderId: string,
  edit: () => Promise<{ orderId: string }>,
  transfer: (previousOrderId: string, replacementOrderId: string) => void,
): Promise<{ orderId: string }> {
  const result = await edit();
  if (result.orderId !== previousOrderId) {
    transfer(previousOrderId, result.orderId);
  }
  return result;
}
